import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createOrUpdateIntegrationSetting,
  getIntegrationSettingByUser,
  deleteIntegrationSettingByUuid,
  getIntegrationSettingByUuid,
  refreshIntegrationSettingAPI,
  getConfigurations,
  getConversations,
} from "./integrationSettingAPI";

export const upsertIntegrationSetting = createAsyncThunk(
  "integrationSetting/upsert",
  async (data, thunkAPI) => {
    try {
      const res = await createOrUpdateIntegrationSetting(data);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationSettingsByUserId = createAsyncThunk(
  "integrationSetting/fetchAllIntegrationSettingsForUser",
  async (search = "", thunkAPI) => {
    try {
      const res = await getIntegrationSettingByUser(search);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const deleteIntegrationSetting = createAsyncThunk(
  "integrationSetting/delete",
  async ({ uuid, deleteMode }, thunkAPI) => {
    try {
      const res = await deleteIntegrationSettingByUuid({
        uuid,
        deleteMode,
      });

      return res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? {
          message: error.message,
        },
      );
    }
  },
);

export const fetchIntegrationSettingById = createAsyncThunk(
  "integrationSetting/fetchIntegrationSettingById",
  async ({ settingUuid }, thunkAPI) => {
    try {
      const res = await getIntegrationSettingByUuid(settingUuid);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const refreshIntegrationSetting = createAsyncThunk(
  "integrationSetting/refreshIntegrationSetting",
  async (arg = {}, thunkAPI) => {
    try {
      const res = await refreshIntegrationSettingAPI(arg?.settingId ?? null);
      const data = res?.data ?? res;

      const failed = data?.results?.find((r) => r.status === "failed");
      if (failed) {
        return thunkAPI.rejectWithValue({
          message: failed.error || "Sync failed. Check your API key.",
        });
      }

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationConfiguration = createAsyncThunk(
  "integrationSetting/fetchIntegrationConfiguration",
  async ({ settingUuid }, thunkAPI) => {
    try {
      const res = await getConfigurations(settingUuid);
      return res?.data ?? res;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

export const fetchIntegrationConversation = createAsyncThunk(
  "integrationSetting/fetchIntegrationConversation",
  async (_, thunkAPI) => {
    try {
      const res = await getConversations();
      const data = res?.data ?? res;
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? { message: error.message },
      );
    }
  },
);

const initialState = {
  setting: null,
  settings: [],
  configurations: null,
  conversations: {},
  conversationGroups: [],
  conversationsLoading: false,
  integrationSettings: [],
  loading: false,
  error: null,
  configLoading: false,
};

const exchangeSlice = createSlice({
  name: "integrationSetting",
  initialState,
  reducers: {
    setSetting: (state, action) => {
      state.setting = action.payload;
    },
    clearSetting: (state) => {
      state.setting = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = !!action.payload;
    },
    updateSettingField: (state, action) => {
      if (state.setting) {
        state.setting = { ...state.setting, ...action.payload };
      }
    },
  },

  extraReducers: (builder) => {
    builder

      .addCase(upsertIntegrationSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertIntegrationSetting.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload;

        const index = state.integrationSettings.findIndex(
          (item) => item.uuid === updated.uuid,
        );

        if (index !== -1) {
          const existing = state.integrationSettings[index];

          state.integrationSettings[index] = {
            ...existing,
            ...updated,
            integrationId:
              typeof existing.integrationId === "object"
                ? existing.integrationId
                : updated.integrationId,
          };
        } else {
          state.integrationSettings.push(updated);
        }

        state.setting = updated;
      })
      .addCase(upsertIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchIntegrationSettingsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationSettingsByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.integrationSettings = action.payload;
      })
      .addCase(fetchIntegrationSettingsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.integrationSettings = [];
        state.error = action.payload;
      })
      .addCase(deleteIntegrationSetting.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteIntegrationSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = null;
        state.error = null;

        const deletedUuid = action.meta.arg.uuid;

        state.integrationSettings = state.integrationSettings.filter(
          (item) => item.uuid !== deletedUuid,
        );
      })
      .addCase(deleteIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationSettingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationSettingById.fulfilled, (state, action) => {
        state.loading = false;
        state.setting = action.payload;
      })
      .addCase(fetchIntegrationSettingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refreshIntegrationSetting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshIntegrationSetting.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(refreshIntegrationSetting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationConfiguration.pending, (state) => {
        state.configLoading = true;
        state.error = null;
      })
      .addCase(fetchIntegrationConfiguration.fulfilled, (state, action) => {
        state.configLoading = false;
        state.configurations = action.payload.configurations;
      })
      .addCase(fetchIntegrationConfiguration.rejected, (state, action) => {
        state.configLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchIntegrationConversation.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(fetchIntegrationConversation.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        const groups = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.conversations || [];

        state.conversationGroups = groups;
      })
      .addCase(fetchIntegrationConversation.rejected, (state, action) => {
        state.conversationsLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSetting,
  clearSetting,
  clearError,
  setLoading,
  updateSettingField,
} = exchangeSlice.actions;

export default exchangeSlice.reducer;
